import { useState, useEffect, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import routes from "@/routes";

import {
  Flex,
  Typography,
  Spin,
  Card,
  App,
  Space,
  Divider,
  ColorPicker,
  Switch,
  Checkbox,
} from "antd";
import { EditOutlined, SettingOutlined } from "@ant-design/icons";

import MainContext from "@/layouts/MainLayout/context";

import DefinitionModal from "@/components/modals/DefinitionModal";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import BackBtn from "@/components/ui/BackBtn";
import PolygonField from "@/components/views/ObjectFieldValue/PolygonField";

import Fields from "./components/fields-card";
import Menu from "./components/menu-card";

import { getItem, updateItem } from "@/api/definitions";
import { getMenu } from "@/api/definitionsMenu";

import {
  TITLES,
  WITHOUT_TEMPLATES,
  PRESETS_COLORS,
  POLYGON,
} from "@/constants/definitions";

const { Title } = Typography;

const Definition = () => {
  const { id, type } = useParams();
  const { message } = App.useApp();

  const { refreshResources } = useContext(MainContext);

  const [data, setData] = useState();
  const [menu, setMenu] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const [isModalShow, setIsModalShow] = useState(false);
  const [modalItem, setModalItem] = useState();

  const [menuPending, setMenuPending] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);

    try {
      const [data, menu] = await Promise.all([getItem(id), getMenu()]);
      setData(data);
      setMenu(menu);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // initial fetch
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [id, type]);

  const handleEdit = () => {
    setModalItem(data);
    setIsModalShow(true);
  };

  const handleSave = () => {
    setIsModalShow(false);
    fetchData();
  };

  const fetchMenu = async () => {
    setMenuPending(true);

    try {
      const menu = await getMenu();

      setMenu(menu);
      refreshResources();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setMenuPending(false);
    }
  };

  const handleColorChange = async (color) => {
    try {
      await updateItem({ uuid: data?.uuid, color: color.toHexString() });

      setData((prev) => ({ ...prev, color: color.toHexString() }));
      message.success("Цвет успешно изменен");
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };
  const handlePermissionChange = async (val) => {
    try {
      await updateItem({ uuid: data?.uuid, use_permission_rules: val });

      setData((prev) => ({ ...prev, use_permission_rules: val }));
      message.success("Изменения сохранены");
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };
  const handleDefaultMapLayerChange = async (event) => {
    const isDefaultMapLayer = event.target.checked;

    try {
      await updateItem({
        uuid: data?.uuid,
        is_default_on_map: isDefaultMapLayer,
      });

      setData((prev) => ({
        ...prev,
        is_default_on_map: isDefaultMapLayer,
      }));
      message.success("Изменения сохранены");
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        data && (
          <>
            <Flex vertical gap={20}>
              <Flex vertical gap={10}>
                <Breadcrumbs
                  items={[
                    {
                      title: "Административная панель",
                    },
                    {
                      title: "Ресурсы",
                    },
                    {
                      title: "Структура объектов",
                    },
                    {
                      title: TITLES[type],
                      href: routes.administration.resourcesSettings.structures.definitions(
                        type,
                      ),
                    },
                    {
                      title: data.name,
                    },
                  ]}
                />

                <BackBtn>{data.name}</BackBtn>
              </Flex>

              <Card
                title="Описание"
                extra={
                  <Link to="#" onClick={handleEdit}>
                    <Space>
                      <EditOutlined />
                      Редактировать
                    </Space>
                  </Link>
                }
              >
                <Flex vertical gap={20}>
                  {data.description ? (
                    <div> {data.description}</div>
                  ) : (
                    <div className="text-secondary">Добавьте описание</div>
                  )}

                  {!WITHOUT_TEMPLATES.includes(type) && (
                    <Flex gap={10} align="center">
                      <Title level={5} className="no-margin">
                        Шаблоны:
                      </Title>
                      {data?.templates?.length ? (
                        <Space
                          size={0}
                          separator={
                            <Divider
                              orientation="vertical"
                              style={{ borderColor: "var(--color-gray)" }}
                            />
                          }
                        >
                          {data.templates.map((item) => (
                            <Link
                              key={item.uuid}
                              to={routes.administration.resourcesSettings.structures.definitionDetail(
                                "templates",
                                item.uuid,
                              )}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </Space>
                      ) : (
                        <>
                          <div className="text-secondary">-</div>
                        </>
                      )}
                    </Flex>
                  )}

                  {type === "objects" && (
                    <>
                      <Flex align="center" gap={10}>
                        <Title level={5} className="no-margin">
                          Единицы измерения:
                        </Title>
                        <div>{data.measure_unit ?? "-"}</div>
                      </Flex>
                      <Flex align="center" gap={10}>
                        <Title level={5} className="no-margin">
                          Расширенный доступ:
                        </Title>
                        <Switch
                          checked={data.use_permission_rules}
                          onChange={handlePermissionChange}
                        />
                      </Flex>
                    </>
                  )}

                  <Flex align="center" gap={10}>
                    <Title level={5} className="no-margin">
                      Актуализация из НСПД:
                    </Title>
                    <div>{data.is_nspd_enabled ? "Включена" : "Отключена"}</div>
                  </Flex>

                  {data.is_nspd_enabled && (
                    <>
                      <Flex align="center" gap={10}>
                        <Title level={5} className="no-margin">
                          Категория ключей НСПД:
                        </Title>
                        <div>{data.nspd_category?.name ?? "-"}</div>
                      </Flex>

                      <Flex align="center" gap={10}>
                        <Title level={5} className="no-margin">
                          Поле кадастрового номера:
                        </Title>
                        <div>{data.nspd_cadastral_field?.name ?? "-"}</div>
                      </Flex>
                    </>
                  )}
                </Flex>
              </Card>

              {/* Поля объекта */}
              <Fields
                definition={data}
                fields={data.fields?.self}
                onChangeObject={fetchData}
              />

              {type !== "templates" && (
                <>
                  {/* Варианты отображения списка */}
                  {type !== "tables" && (
                    <Card title="Отображение списка объектов">
                      <Flex vertical gap={10} align="start">
                        <Link
                          to={routes.administration.resourcesSettings.structures.definitionSettingTabularForm(
                            type,
                            id,
                          )}
                        >
                          <Space>
                            <SettingOutlined />
                            Настроить видимые поля в таблице
                          </Space>
                        </Link>
                        <Link
                          to={routes.administration.resourcesSettings.structures.definitionSettingFilters(
                            type,
                            id,
                          )}
                        >
                          <Space>
                            <SettingOutlined />
                            Настроить группы фильтров
                          </Space>
                        </Link>
                      </Flex>
                    </Card>
                  )}

                  <Card title="Отображение объекта">
                    <Flex vertical gap={16} align="start">
                      <ColorPicker
                        defaultValue={data.color}
                        size="small"
                        disabledAlpha
                        presets={[
                          {
                            label: "Цвета",
                            colors: PRESETS_COLORS,
                          },
                        ]}
                        showText={(color) => (
                          <span>Цвет объектов на карте</span>
                        )}
                        styles={{ popupOverlayInner: { width: 780 } }}
                        panelRender={(
                          _,
                          { components: { Picker, Presets } },
                        ) => (
                          <Flex gap={10}>
                            <Flex vertical gap={10}>
                              <Presets />
                              <Picker />
                            </Flex>

                            <PolygonField value={POLYGON} color={data.color} />
                          </Flex>
                        )}
                        onChangeComplete={handleColorChange}
                      />

                      {type === "objects" && (
                        <Checkbox
                          checked={Boolean(data.is_default_on_map)}
                          onChange={handleDefaultMapLayerChange}
                        >
                          Слой по умолчанию на карте
                        </Checkbox>
                      )}

                      <Link
                        to={routes.administration.resourcesSettings.structures.definitionSettingObjectForm(
                          type,
                          id,
                        )}
                      >
                        <Space>
                          <SettingOutlined />
                          Настроить вкладки и поля
                        </Space>
                      </Link>
                    </Flex>
                  </Card>

                  <Menu
                    definition={data}
                    menu={menu}
                    pending={menuPending}
                    onChange={fetchMenu}
                  />
                </>
              )}
            </Flex>

            {isModalShow && (
              <DefinitionModal
                data={modalItem}
                visible={isModalShow}
                onSave={handleSave}
                onCancel={() => setIsModalShow(false)}
              />
            )}
          </>
        )
      )}
    </>
  );
};

export default Definition;
